import { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../../firebase';
import type { Application } from '../../../types/application';
import { FormData, ValidationErrors, ToastState } from '../types';
import { validateForm } from '../utils';


export const useOrderDetailForm = (
  application: Application | null,
  adminId: string,
  onUpdate?: (updatedApplication: Partial<Application>) => void,
  onClose?: () => void
) => {
  // 🎯 Form 관리
  const [formData, setFormData] = useState<FormData>({
    status: '',
    priceOffer: '',
    finalPrice: '',
    remark: '',
    trackingNumber: '',
    storeCode: '',
    grade: '',
    deductionReason: '',
  });
  
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // 🎯 초기 데이터 로딩
  useEffect(() => {
    if (application) {
      setFormData({
        status: application.status || '',
        priceOffer: application.price_offer?.toString() || '',
        finalPrice: application.final_price?.toString() || '',
        remark: application.remark || '',
        trackingNumber: application.tracking_number || '',
        storeCode: application.store_code || '',
        grade: application.grade || '',
        deductionReason: application.deduction_reason || '',
      });
    }
  }, [application]);

  // 📊 변경사항 감지
  const hasChanges = useMemo(() => {
    if (!application) return false;
    return (
      formData.status !== (application.status || '') ||
      formData.priceOffer !== (application.price_offer?.toString() || '') ||
      formData.finalPrice !== (application.final_price?.toString() || '') ||
      formData.remark !== (application.remark || '') ||
      formData.trackingNumber !== (application.tracking_number || '') ||
      formData.storeCode !== (application.store_code || '') ||
      formData.grade !== (application.grade || '') ||
      formData.deductionReason !== (application.deduction_reason || '')
    );
  }, [application, formData]);

  // 🎊 토스트 관리
  const showToast = useCallback((message: string, severity: ToastState['severity'] = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, open: false }));
  }, []);

  // 📝 메모 변경 핸들러 (메모이제이션)
  const handleRemarkChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFormData(prev => ({ ...prev, remark: newValue }));
  }, []);

  // 💾 저장 기능 (직접 데이터 전달 가능)
  const handleSave = useCallback(async (overrideData?: Partial<FormData>) => {
    if (!application?.id) return;
    
    // 🎯 직접 전달된 데이터가 있으면 강제 저장, 없으면 변경사항 확인
    const finalFormData = overrideData ? { ...formData, ...overrideData } : formData;
    const shouldSave = overrideData || hasChanges;
    
    if (!shouldSave) return;
    
    setLoading(true);
    try {
      const applicationRef = doc(db, 'applications', application.id);
      const updateData: any = {
        updated_at: serverTimestamp(),
        updated_by: adminId,
      };

      // 🔄 전체 상태 변경 시 하위 기기 상태도 함께 변경
      const oldStatus = application.status || '';
      const newStatus = finalFormData.status;
      
      if (newStatus !== oldStatus) {
        updateData.status = newStatus;
        
        // 전체 상태가 접수완료/택배접수/입고완료 → 검수중으로 변경될 때
        // 모든 기기 상태를 해당 단계에 맞게 설정
        if (application.devices && application.devices.length > 0) {
          const updatedDevices = application.devices.map(device => {
            let deviceStatus = device.status;
            
            // 🔄 전체 상태에 따른 기기 상태 동기화 (완전 매핑)
            // 🔒 반송 상태인 기기는 변경하지 않음
            if (device.status === '반송') {
              deviceStatus = device.status; // 현재 상태 유지
            } else {
              switch (newStatus) {
                case '접수완료':
                case '택배접수':
                case '입고완료':
                case '검수중':
                case '가격안내':
                case '입금대기':
                case '반송':
                case '완료':
                  // 하위 기기 상태에 존재하는 상태들은 직접 매핑
                  deviceStatus = newStatus;
                  break;
                case '부분완료':
                  // 부분완료는 개별 기기 상태가 아니므로 기기 상태 변경하지 않음
                  deviceStatus = device.status;
                  break;
                default:
                  // 기타 상태는 현재 상태 유지
                  deviceStatus = device.status;
                  break;
              }
            }
            
            return {
              ...device,
              status: deviceStatus,
              updated_at: new Date()
            };
          });
          
          updateData.devices = updatedDevices;
        }
      }
      if (finalFormData.grade !== (application.grade || '')) {
        updateData.grade = finalFormData.grade;
      }
      if (finalFormData.priceOffer !== (application.price_offer?.toString() || '')) {
        updateData.price_offer = finalFormData.priceOffer === '' ? 0 : Number(finalFormData.priceOffer);
      }
      if (finalFormData.finalPrice !== (application.final_price?.toString() || '')) {
        updateData.final_price = finalFormData.finalPrice === '' ? 0 : Number(finalFormData.finalPrice);
      }

      // ✅ 요구사항 2: 상태를 완료로 변경할 때 최종가격이 비어있으면 검수금액을 최종가격으로 자동 설정
      if (newStatus === '완료') {
        const finalPriceNumber = Number(finalFormData.finalPrice || 0);
        const priceOfferNumber = Number(finalFormData.priceOffer || 0);
        if (!finalPriceNumber || finalPriceNumber === 0) {
          updateData.final_price = priceOfferNumber;
        }
        
        // 🔄 기기 상태도 '완료'로 변경하고, final_price가 없으면 price_offer로 보정
        if (application.devices && application.devices.length > 0) {
          const updatedDevices = application.devices.map(device => {
            let deviceStatus = device.status;
            let deviceFinalPrice = device.final_price;
            
            // 🔒 반송 상태인 기기는 변경하지 않음
            if (device.status === '반송') {
              deviceStatus = device.status; // 현재 상태 유지
            } else {
              deviceStatus = newStatus;
              // ✅ 기기별로도 final_price가 없으면 price_offer로 보정
              if (!deviceFinalPrice || deviceFinalPrice === 0) {
                deviceFinalPrice = device.price_offer;
              }
            }
            
            return {
              ...device,
              status: deviceStatus,
              final_price: deviceFinalPrice,
              updated_at: new Date()
            };
          });
          
          updateData.devices = updatedDevices;
        }
      }
      if (finalFormData.trackingNumber !== (application.tracking_number || '')) {
        updateData.tracking_number = finalFormData.trackingNumber;
      }
      if (finalFormData.storeCode !== (application.store_code || '')) {
        updateData.store_code = finalFormData.storeCode;
      }
      if (finalFormData.remark !== (application.remark || '')) {
        updateData.remark = finalFormData.remark;
      }
      if (finalFormData.deductionReason !== (application.deduction_reason || '')) {
        updateData.deduction_reason = finalFormData.deductionReason;
      }

      await updateDoc(applicationRef, updateData);
      
      // 성공 시 상위 컴포넌트에 업데이트 알림
      if (onUpdate) {
        onUpdate(updateData);
      }
      
      showToast('신청서가 성공적으로 업데이트되었습니다', 'success');
      
      // 🎯 성공 후 Drawer 자동 닫기 (사용자가 메시지를 볼 수 있도록 딜레이)
      if (onClose) {
        setTimeout(() => {
          hideToast(); // 토스트 먼저 숨기기
          onClose();
        }, 1200);
      }
      
    } catch (error) {
      console.error('Save error:', error);
      showToast('저장 중 오류가 발생했습니다', 'error');
    } finally {
      setLoading(false);
    }
  }, [application, formData, hasChanges, adminId, onUpdate, onClose, showToast, hideToast]);

  // 🗑️ 삭제 기능
  const handleDelete = useCallback(async (onClose: () => void) => {
    if (!application?.id) return;
    
    // 🚫 완료된 신청건 삭제 불가
    if (application.status === '완료') {
      showToast('완료된 신청건은 삭제할 수 없습니다', 'error');
      setConfirmOpen(false);
      return;
    }
    
    setLoading(true);
    try {
      const applicationRef = doc(db, 'applications', application.id);
      await deleteDoc(applicationRef);
      
      showToast('신청서가 삭제되었습니다', 'success');
      setConfirmOpen(false);
      onClose(); // 삭제 후 drawer 닫기
      
    } catch (error) {
      console.error('Delete error:', error);
      showToast('삭제 중 오류가 발생했습니다', 'error');
    } finally {
      setLoading(false);
    }
  }, [application, showToast]);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    loading,
    hasChanges,
    confirmOpen,
    setConfirmOpen,
    toast,
    showToast,
    hideToast,
    handleRemarkChange,
    handleSave,
    handleDelete,
  };
}; 